import * as React from 'react'
import * as Redux from 'react-redux'
import { type Dispatch } from 'redux'

import { renderConfetti } from '@owl-nest/confetti'
import * as model from '@owl-nest/models'
import * as Enum from '@owl-nest/legacy-enum'

import { useOnboardingStep } from './useOnboardingStep'
import * as projectAction from '../../../actions/project'

const ONBOARDING_NAMESPACE = 'onboarding'
const COMMUNITY_NAMESPACE = 'import_community'

export const Step = Enum.create({
  WELCOME: 'welcome',
  PROJECT_TYPE: 'project_type',
  IMPORT_TYPE: 'import_type',
  PROFILE: 'profile',
  CATEGORY: 'category',
  TRAINING: 'training',
  REWARD_TYPE: 'reward_type',
  SLUG: 'slug',
  DONE: 'done',
})
export type Step = Enum.ValueOf<typeof Step>

const STEP_ORDER = [
  Step.WELCOME,
  Step.PROJECT_TYPE,
  Step.IMPORT_TYPE,
  Step.CATEGORY,
  Step.REWARD_TYPE,
  Step.TRAINING,
  Step.PROFILE,
  Step.SLUG,
] as Step[]

type NAMESPACE = typeof ONBOARDING_NAMESPACE | typeof COMMUNITY_NAMESPACE

const STEP_NAMESPACES: Record<Step, NAMESPACE> = {
  [Step.WELCOME]: ONBOARDING_NAMESPACE,
  [Step.PROJECT_TYPE]: ONBOARDING_NAMESPACE,
  [Step.CATEGORY]: ONBOARDING_NAMESPACE,
  [Step.REWARD_TYPE]: ONBOARDING_NAMESPACE,
  [Step.TRAINING]: ONBOARDING_NAMESPACE,
  [Step.PROFILE]: ONBOARDING_NAMESPACE,
  [Step.SLUG]: ONBOARDING_NAMESPACE,
  [Step.DONE]: ONBOARDING_NAMESPACE,
  [Step.IMPORT_TYPE]: COMMUNITY_NAMESPACE,
}

let isGlobalDone = false

export function getStepIndex(step: Step): string {
  const index = STEP_ORDER.indexOf(step)
  // remove 1 everywhere because `Step.WELCOME` does not count (so there is only 5 steps, CATEGORY being n° 1)
  return `${index} / ${STEP_ORDER.length - 1}`
}

export function getStepProgress(step: Step): number {
  const index = STEP_ORDER.indexOf(step)
  // remove 1 everywhere because `Step.WELCOME` does not count (so there is only 5 steps, CATEGORY being n° 1)
  return index / (STEP_ORDER.length - 1)
}

export function useStep(project: model.project.ProjectForOwner) {
  const dispatch = Redux.useDispatch<Dispatch>()
  const steps = computeSteps(project)
  const step = getCurrentStep()
  const isMembership = model.project.isMembership(project)

  const setOnboardingStep = useOnboardingStep({
    project,
    namespace: ONBOARDING_NAMESPACE,
    setOnboardingSteps: (nextSteps, extra) => {
      if (isDone()) {
        return dispatch(projectAction.update(project, { ...extra, onboarding_steps: nextSteps })).then(() => {
          endCallback()
        })
      }

      return dispatch(projectAction.update(project, { ...extra, onboarding_steps: nextSteps }))
    },
  })
  const setImportCommunityStep = useOnboardingStep({
    project,
    namespace: COMMUNITY_NAMESPACE,
    setOnboardingSteps: (nextSteps, extra) => {
      return dispatch(projectAction.update(project, { ...extra, onboarding_steps: nextSteps }))
    },
  })

  React.useEffect(() => {
    // Inject or subtract slug/title step based on project type
    if (STEP_ORDER.includes(Step.SLUG) && isMembership) {
      STEP_ORDER.pop()
    } else if (!STEP_ORDER.includes(Step.SLUG) && !isMembership) {
      STEP_ORDER.push(Step.SLUG)
    }

    // All computed steps where done
    if (step === undefined && !isDone()) {
      isGlobalDone = true
      setOnboardingStep(Step.DONE, true)
    }
  }, [step])

  return { step, isDone: isDone(), setStepValue }

  function endCallback() {
    if (model.project.isMembership(project)) {
      window.location.href = `/${project.slug}/?confetti=true`
    } else {
      renderConfetti()
    }
  }

  async function setStepValue(extra?: model.project.UpdateProject, value: any = true): Promise<void> {
    const step = getCurrentStep()
    if (step === undefined) {
      return
    }

    if (step.namespace === 'onboarding') {
      await setOnboardingStep(step.key, value, extra)
    } else {
      await setImportCommunityStep(step.key, value, extra)
    }
  }

  function isDone(): boolean {
    return (isGlobalDone || project.onboarding_steps?.[ONBOARDING_NAMESPACE]?.[Step.DONE]) ?? false
  }

  function getCurrentStep(): { key: Step; namespace: NAMESPACE } | undefined {
    const markedSteps = {
      ...(project.onboarding_steps?.[ONBOARDING_NAMESPACE] ?? {}),
      ...(project.onboarding_steps?.[COMMUNITY_NAMESPACE] ?? {}),
    }

    const lastStepIndex = (Object.keys(markedSteps) as Step[]).reduce((lastStepIndex, step) => {
      const stepIndex = steps.indexOf(step)
      if (stepIndex > lastStepIndex) {
        return stepIndex
      }
      return lastStepIndex
    }, -1)

    const currentStepKey = steps[lastStepIndex + 1]

    if (currentStepKey === undefined) {
      return undefined
    }

    return { key: currentStepKey, namespace: STEP_NAMESPACES[currentStepKey] }
  }
}

function computeSteps(project: model.project.ProjectForOwner): Step[] {
  const steps = [...STEP_ORDER]

  // If project already has a main tag, we skip the category step
  if (project.main_tag !== undefined) {
    const index = steps.indexOf(Step.CATEGORY)
    steps.splice(index, 1)
  }

  // If project already as a presale type or a goal, we skip the project type step
  if (project.type === 'presale' || project.goal > 0) {
    const index = steps.indexOf(Step.PROJECT_TYPE)
    steps.splice(index, 1)
  }

  // If project already as a donation/presale/membership type, or a goal, we skip the reward step
  if (['donation', 'membership', 'presale'].includes(project.type) || project.goal > 0) {
    const index = steps.indexOf(Step.REWARD_TYPE)
    steps.splice(index, 1)
  }

  // If project is not a membership or project has 0 community_members_count, we skip the project community step
  if (project.type !== 'membership' || project.community_members_count === 0) {
    const index = steps.indexOf(Step.IMPORT_TYPE)
    steps.splice(index, 1)
  }

  // If project is not french, we skip the training step
  if (project.lang !== 'fr') {
    const index = steps.indexOf(Step.TRAINING)
    steps.splice(index, 1)
  }

  // If project owner already has an avatar and screenname, we skip the profile step
  if (project.owner.has_avatar && project.owner.screenname) {
    const index = steps.indexOf(Step.PROFILE)
    steps.splice(index, 1)
  }

  return steps
}
